import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Constants } from '@base/src/app/models/constants';
import { QRButton } from '@base/src/app/models/qr-button.model';
import { QRDatePicker } from '@base/src/app/models/qr-date-picker.model';
import { QRDropdown } from '@base/src/app/models/qr-dropdown.model';
import { QRInput } from '@base/src/app/models/qr-input.model';
import { QRToggle } from '@base/src/app/models/qr-toggle.model';
import { GetPaymentTypesService } from '@base/src/app/pages/movements/services/get-payment-types.service';
import { SnackbarService } from '@base/src/app/services/snackbar.service';
import { UtilsService } from '@base/src/app/services/utils.service';
import * as moment from 'moment';
import { MovementsService } from '../../../services/movements.service';
import { GetAgencyTcService } from '../../../services/get-agency-tc.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from '@base/environments/environment';
import { Family } from '../../../models/family.model';
import { PaymentType } from '../../../models/paymentType.model';
import { LogedUser } from '@base/src/app/core/models/logged-user.model';

type InputTypes = string | number | undefined;

class Income {
  userId!: string;
  agencyId!: number;
  id!: number;
  familyId!: number;
  subFamilyId!: number;
  incomeDate?: string;
  expenseDate?: string;
  depositDate?: string;
  concept!: string;
  description!: string;
  voucherType!: number;
  paymentType!: number;
  currency!: string;
  exchangeRate!: number;
  subTotal!: number;
  iva!: number;
  subTotalIva!: number;
  perceivedIIBB!: number;
  others?: number;
  total!: number;
  invoiceNumber!: string | null;
  familyTxt!: string | null;
  subFamilyTxt!: string | null;
  voucherTypeTxt!: string | null;
  paymentTypeTxt!: string | null;
  renditionId!: number;
  anulacionId!: number;
  anulacion!: boolean;
  annulmentEnabled!: boolean;
  accountMovementId!: number;
}

@Component({
  selector: 'app-dialog-add-income',
  templateUrl: './dialog-add-income.component.html',
  styleUrls: ['./dialog-add-income.component.scss'],
})
export class DialogAddIncomeComponent implements OnInit {
  readonly NODE = environment.node;
  readonly NODE_AR = Constants.TENANT_ID_AR;
  readonly NODE_DO = Constants.TENANT_ID_DO;
  readonly NODE_EC = Constants.TENANT_ID_EC;
  readonly NODE_PY = Constants.TENANT_ID_PY;

  // Constants
  readonly INTERNATIONAL_CURRENCY: string = Constants.INTERNATIONAL_CURRENCY;
  readonly DOLLAR_CURRENCY: string = Constants.CURRENCY_DOLLAR;
  readonly LOCAL_CURRENCY: string = Constants.LOCAL_CURRENCY_SIMBOL;

  @Input() title: string = '';
  @Input() agencyId: number | undefined = 0;
  @Input() userId: string | undefined = '';
  @Input() cashflows: Record<string, boolean> = {};
  /** Prefetched from movements panel; falls back to catalog fetch if empty. */
  @Input() voucherTypes: PaymentType[] = [];
  @Output() formSubmit = new EventEmitter<boolean>();
  @Output() close: EventEmitter<void> = new EventEmitter<void>();

  loggedUser: LogedUser | undefined;
  isLoading: boolean = false;
  isExpense: boolean = false;
  typeChange: number = 0;

  buttonMovement: QRButton = {
    hierarchy: QRButton.HIERARCHY_2,
    size: QRButton.SIZE_MEDIUM,
    icon: Constants.ICONS.INPUT,
    iconOnly: true,
  };

  buttonClose: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_SMALL,
    icon: Constants.ICONS.CLOSE,
    iconOnly: true,
  };

  datePickerIncomeDate: QRDatePicker = {
    date: undefined,
    hint: '',
    calendarWidth: 160,
    isEmptyDateAllowed: true,
  };

  datePickerDepositDate: QRDatePicker = {
    date: undefined,
    hint: '',
    calendarWidth: 160,
    isEmptyDateAllowed: true,
  };

  dropdownFamilies: QRDropdown = {
    hierarchy: QRDropdown.HIERARCHY_1,
    size: QRDropdown.SIZE_MEDIUM,
    placeholder: 'Seleccionar',
    options: [],
  };

  dropdownSubFamilies: QRDropdown = {
    hierarchy: QRDropdown.HIERARCHY_1,
    size: QRDropdown.SIZE_MEDIUM,
    placeholder: 'Seleccionar',
    options: [],
  };

  dropdownPayment: QRDropdown = {
    hierarchy: QRDropdown.HIERARCHY_1,
    size: QRDropdown.SIZE_MEDIUM,
    placeholder: 'Seleccionar',
    options: [],
  };

  dropdownVoucherType: QRDropdown = {
    hierarchy: QRDropdown.HIERARCHY_1,
    size: QRDropdown.SIZE_MEDIUM,
    placeholder: 'Seleccionar',
    options: [],
  };

  dropdownFamily: QRDropdown = {
    hierarchy: QRDropdown.HIERARCHY_1,
    size: QRDropdown.SIZE_MEDIUM,
    placeholder: 'Seleccionar',
    options: [],
  };

  inputConcept: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: 'Ingrese en concepto...',
    debounceTime: 500,
  };

  inputInvoiceNumber: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: '0',
  };

  toggleCurrency: QRToggle = {
    hierarchy: QRToggle.HIERARCHY_1,
    size: QRToggle.SIZE_MEDIUM,
    viewValues: ['U$S', '$'],
    optionSelected: 0,
  };

  inputSubtotal: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: '0',
  };

  inputTc: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: '0',
    errorMsg: 'TC=1 o menor no es válido, ingrese el TC real.',
  };

  inputIva: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: '0',
    hint: 'U$S',
  };

  inputPerception: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: '0',
    hint: 'U$S',
  };

  inputOthers: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: '0',
    hint: 'U$S',
  };

  buttonCloseOverlay: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Cancelar',
  };

  buttonAddMovement: QRButton = {
    hierarchy: QRButton.HIERARCHY_1,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Agregar movimiento',
    loader: QRButton.LOADER_SHIMMER,
    loaderOnlyWhenSubmitReady: true,
  };

  income!: Income;
  isCheckPayment: boolean = false;

  families: Family[] = [];
  subFamilies: Family[] = [];
  subFamilySelected: string = '';
  isSubFamilyEmpty: boolean = false;
  paymentTypes: PaymentType[] = [];
  signalCurrency: string = this.INTERNATIONAL_CURRENCY;
  todayDate = moment().format('YYYY-MM-DD');

  get isSubmitReady(): boolean {
    return !this.hasFormErrors().hasErrors;
  }

  movementForm: FormGroup = new FormGroup({
    incomeDate: new FormControl(''),
    family: new FormControl('', Validators.required),
    subFamily: new FormControl(''),
    concept: new FormControl('', Validators.required),
    description: new FormControl('', Validators.required),
    paymentType: new FormControl('', Validators.required),
    depositDate: new FormControl(''),
    voucherType: new FormControl('', Validators.required),
    subTotal: new FormControl('', Validators.required),
    exchangeRate: new FormControl(1, [Validators.required, Validators.min(1)]), //tipo de cambio
  });


  constructor(
    public readonly utils: UtilsService,
    private readonly movementsService: MovementsService,
    private readonly getPaymentTypes: GetPaymentTypesService,
    private readonly getAgencyTc: GetAgencyTcService,
    private readonly snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.getLoggedUser();
    this.isExpense = this.title === 'Egreso';
    if (this.isExpense) this.buttonMovement.icon = Constants.ICONS.OUTPUT;
    if (this.NODE === this.NODE_EC) {
      this.toggleCurrency.viewValues = ['U$S'];
    }
    this.income = new Income();
    this.income.agencyId = this.agencyId ?? 0;
    this.income.userId = this.userId ?? '';
    this.income.id = 0;
    this.income.incomeDate = this.todayDate;
    this.income.currency = this.DOLLAR_CURRENCY;
    this.income.perceivedIIBB = 0;
    this.formatDropdownIncomeDate();
    this.getFamiliesList();
    this.getPaymentTypesList();
    this.initVoucherTypes();
    this.getAgencyTypeChange();
  }

  getLoggedUser(): void {
    this.loggedUser = this.utils.getLoggedUser();
  }

  getFamiliesList(): void {
    const TYPE = this.isExpense ? 'expense' : 'income';
    const URL_FAMILY = `family/${TYPE}/list/`;

    this.movementsService
      .get(URL_FAMILY)
      .then((response) => {
        if (!response) return;
        const FILTERED =
          this.loggedUser?.security.movements_fam_socios === false
            ? response.filter(
                (f: { name: string }) => f.name.toLowerCase() !== 'socios',
              )
            : response;

        this.families = FILTERED;
        this.dropdownFamilies.options = FILTERED.map(
          (f: { name: string }) => (f.name === 'Maxcenter' ? 'Membresía Anual' : f.name),
        );
        this.dropdownFamilies = { ...this.dropdownFamilies };
      })
      .catch(() => {
        this.snackbarService.openSnackBar(
          '❌ Error al cargar las familias.',
          '',
        );
      });
  }

  getPaymentTypesList(): void {
    const AGENCY_ID: number = this.agencyId ?? 0;
    const URL_PAYMENT_TYPES: string = `paymenttypes/${AGENCY_ID}`;
    this.getPaymentTypes
      .get(URL_PAYMENT_TYPES)
      .then((response) => {
        if (!response) return;

        const CASHFLOWS_FLAGS: { [key: string]: boolean } = {
          '8': this.cashflows['movements_custom_cash_flow'],
          '9': this.cashflows['movements_custom_cash_flow_2'],
          '10': this.cashflows['movements_custom_cash_flow_3'],
          '11': this.cashflows['movements_custom_cash_flow_4'],
          '12': this.cashflows['movements_custom_cash_flow_5'],
          '13': this.cashflows['movements_custom_cash_flow_6'],
        };

        this.paymentTypes = response.filter((pt: PaymentType) => {
          if (pt.id <= 7) return true;
          return CASHFLOWS_FLAGS[pt.id] === true;
        });

        this.dropdownPayment.options = this.paymentTypes.map(
          (f: { label: string }) => f.label,
        );
        this.dropdownPayment = { ...this.dropdownPayment };
      })
      .catch(() => {
        this.snackbarService.openSnackBar('❌ Error al cargar las cajas.', '');
      });
  }

  initVoucherTypes(): void {
    if (this.voucherTypes?.length) {
      this.applyVoucherTypesToDropdown();
      return;
    }
    this.getVoucherTypes();
  }

  getVoucherTypes(): void {
    const URL_VOUCHER_TYPES: string = `vouchertypes`;
    this.getPaymentTypes
      .get(URL_VOUCHER_TYPES)
      .then((response) => {
        this.voucherTypes = response ?? [];
        this.applyVoucherTypesToDropdown();
      })
      .catch(() => {
        this.snackbarService.openSnackBar(
          '❌ Error al cargar los tipos de comprobantes.',
          '',
        );
      });
  }

  private applyVoucherTypesToDropdown(): void {
    this.dropdownVoucherType.options = this.voucherTypes.map(
      (f: { label: string }) => f.label,
    );
    this.dropdownVoucherType = { ...this.dropdownVoucherType };
  }

  getAgencyTypeChange(): void {
    const URL_EXCHANGE_RATE: string = `account/tc/get/${this.agencyId}`;
    this.getAgencyTc
      .get(URL_EXCHANGE_RATE)
      .then((response) => {
        this.typeChange = response.data.tc;
      })
      .catch(() => {
        this.snackbarService.openSnackBar(
          '❌ Error al cargar tipo de cambio.',
          '',
        );
      });
  }

  addIncomeDate(selected: string): void {
    if (!selected) {
      this.movementForm.get('incomeDate')?.setErrors({ required: true });
      return;
    }

    this.setDataInForm('incomeDate', selected);

    if (!this.isExpense) {
      this.income.incomeDate = selected;
    } else {
      this.income.expenseDate = selected;
    }
  }

  selectFamily(selected: string): void {
    this.setDataInForm('family', selected ?? '');
    this.setDataInForm('subFamily', '');

    if(selected === 'Membresía Anual') {
      selected = 'Maxcenter';
    }
  
    this.subFamilySelected = '';
    this.subFamilies = [];
    this.dropdownSubFamilies.options = [];
    this.dropdownSubFamilies.placeholder = 'Seleccionar';
    this.dropdownSubFamilies = { ...this.dropdownSubFamilies };
    // Keep disabled until the current family's subfamilies finish loading.
    this.isSubFamilyEmpty = true;
    this.income.subFamilyId = 0;
    this.income.subFamilyTxt = null;
    // Block submit until subfamilies load confirms whether the field is required.
    this.markRequired('subFamily');

    const SELECTED = this.families.find((c) => c.name === selected);
    const SELECTED_ID = SELECTED?.id ?? null;
    this.income.familyId = SELECTED_ID ?? 0;
    this.income.familyTxt = selected;

    const REQUESTED_FAMILY_ID = this.income.familyId;

    const SUBFAMILY_TYPE = this.isExpense
      ? 'subfamilyExpense'
      : 'subfamilyIncome';

    const URL_SUBFAMILY: string = `family/${SELECTED_ID}/${SUBFAMILY_TYPE}/list/`;

    this.movementsService
      .get(URL_SUBFAMILY)
      .then((response) => {
        if (this.income.familyId !== REQUESTED_FAMILY_ID) {
          return;
        }
        this.subFamilies = response ?? [];
        this.dropdownSubFamilies.options = this.subFamilies.map(
          (f: { name: string }) => f.name,
        );

        if (this.dropdownSubFamilies.options.length === 0) {
          this.dropdownSubFamilies.placeholder = 'Ninguna';
          this.isSubFamilyEmpty = true;
          this.removeRequired('subFamily');
        } else {
          this.dropdownSubFamilies.placeholder = 'Seleccionar';
          this.isSubFamilyEmpty = false;
          this.markRequired('subFamily');
        }

        this.dropdownSubFamilies = { ...this.dropdownSubFamilies };
      })
      .catch(() => {
        if (this.income.familyId !== REQUESTED_FAMILY_ID) {
          return;
        }
        this.subFamilies = [];
        this.dropdownSubFamilies.options = [];
        this.dropdownSubFamilies = { ...this.dropdownSubFamilies };
        this.isSubFamilyEmpty = true;
        this.markRequired('subFamily');
        this.snackbarService.openSnackBar(
          '❌ Error al cargar las sub-familias.',
          '',
        );
      });
  }

  selectSubFamily($event: string): void {
    this.subFamilySelected = $event;
    this.setDataInForm('subFamily', $event ?? '');
    const SELECTED = this.subFamilies.find((c) => c.name === $event);
    const SELECTED_ID = SELECTED?.id ?? 0;
    this.income.subFamilyId = SELECTED_ID;
    this.income.subFamilyTxt = $event;
  }

  inputChangeConcept(value: InputTypes): void {
    this.setDataInForm('concept', value ?? '');
    this.income.concept = value ? value.toString() : '';
  }

  textareaChange(value: InputTypes): void {
    const DESCRIPTION: string = value ? value.toString() : '';
    this.setDataInForm('description', DESCRIPTION);
    this.income.description = DESCRIPTION;
  }

  selectPaymentType(selected: string): void {
    this.isCheckPayment = selected === 'Cheque';
    const SELECTED = this.paymentTypes.find((c) => c.label === selected);
    const SELECTED_ID = SELECTED?.id ?? 0;
    this.income.paymentType = SELECTED_ID;
    this.setDataInForm('paymentType', selected);
    this.income.paymentTypeTxt = selected;
  }

  addDepositDate(selected: string): void {
    this.setDataInForm('depositDate', selected ?? '');
    this.income.depositDate = selected;
  }

  selectVoucherType(selected: string): void {
    const SELECTED = this.voucherTypes.find((c) => c.label === selected);
    const SELECTED_ID = SELECTED?.id ?? 0;
    this.income.voucherType = SELECTED_ID;
    this.setDataInForm('voucherType', selected);
    this.income.voucherTypeTxt = selected;

    if (this.isExpense && SELECTED_ID === this.voucherTypeScId) {
      this.income.iva = 0;
      this.income.perceivedIIBB = 0;
    }
  }

  changeInvoiceNumber(value: InputTypes): void {
    if (value === undefined || value === null || value === '') {
      this.income.invoiceNumber = '';
      return;
    }

    this.income.invoiceNumber = value.toString().replace(/[^0-9-]/g, '');
  }

  changeExchangeRate(value: InputTypes): void {
    const EXCHANGE_RATE = value ? Number(value) : 0;
    this.income.exchangeRate = EXCHANGE_RATE;
    this.setDataInForm('exchangeRate', EXCHANGE_RATE);
  }

  changeCurrency($event: number): void {
    if ($event === 0) {
      this.income.currency = this.DOLLAR_CURRENCY;
      this.inputIva.hint = this.INTERNATIONAL_CURRENCY;
      this.inputPerception.hint = this.INTERNATIONAL_CURRENCY;
      this.inputOthers.hint = this.INTERNATIONAL_CURRENCY;
      this.signalCurrency = this.INTERNATIONAL_CURRENCY;
    } else {
      this.income.currency = this.getLocalCurrency();
      this.inputIva.hint = this.LOCAL_CURRENCY;
      this.inputPerception.hint = this.LOCAL_CURRENCY;
      this.inputOthers.hint = this.LOCAL_CURRENCY;
      this.signalCurrency = this.LOCAL_CURRENCY;
    }
  }

  changeSubtotal(value: InputTypes): void {
    if (value === undefined || value === null || value === '') {
      this.setDataInForm('subTotal', 0);
      this.income.subTotal = 0;
      return;
    }

    const RESULT = this.cleanInput(value);

    this.setDataInForm('subTotal', RESULT);
    this.income.subTotal = RESULT;
  }

  changeIvaAmount(value: InputTypes): void {
    if (value === undefined || value === null || value === '') {
      this.income.iva = 0;
      return;
    }

    const RESULT = this.cleanInput(value);
    this.income.iva = RESULT;
  }

  addPerception(value: InputTypes): void {
    if (value === undefined || value === null || value === '') {
      this.income.perceivedIIBB = 0;
      return;
    }

    const RESULT = this.cleanInput(value);
    this.income.perceivedIIBB = RESULT;
  }

  addOthers(value: InputTypes): void {
    if (value === undefined || value === null || value === '') {
      this.income.others = 0;
      return;
    }

    const RESULT = this.cleanInput(value);
    this.income.others = RESULT;
  }

  getTotal(): number {
    const IVA = this.income?.iva ? Number(this.income.iva) : 0;
    const IIBB = this.income.perceivedIIBB
      ? Number(this.income.perceivedIIBB)
      : 0;
    const OTHERS = this.income.others ? Number(this.income.others) : 0;
    const SUB_TOTAL = this.income?.subTotal ? Number(this.income.subTotal) : 0;
    return IVA + IIBB + OTHERS + SUB_TOTAL;
  }

  showExpenseIibbAndOthersFields(): boolean {
    if (!this.isExpense) {
      return false;
    }

    return (
      this.NODE === this.NODE_AR ||
      this.NODE === this.NODE_EC ||
      this.NODE === this.NODE_PY
    );
  }

  formatDropdownIncomeDate(): void {
    this.datePickerIncomeDate.date = this.todayDate;
    this.datePickerIncomeDate = { ...this.datePickerIncomeDate };
    this.income.incomeDate = this.todayDate;
  }

  getLocalCurrency(): string {
    switch (environment.node) {
      case Constants.TENANT_ID_AR:
        return Constants.CURRENCY_PESOS_ARS;
      case Constants.TENANT_ID_PY:
        return Constants.CURRENCY_PESOS_PYG;
      default:
        return Constants.CURRENCY_PESOS_ARS;
    }
  }

  async addMovement(): Promise<void> {
    if (this.isLoading) return;

    this.movementForm.markAllAsTouched();

    if (this.hasFormErrors().hasErrors) {
      this.scrollToElementById(this.hasFormErrors().firstInputErrorID);
      this.showError('Faltan campos por completar');
      return;
    }

    this.isLoading = true;
    if (!this.isExpense) {
      try {
        const URL_CALCULATE_INCOME: string = `income/calculate/`;
        const RESPONSE = await this.movementsService.post(
          this.income,
          URL_CALCULATE_INCOME,
        );

        const INCOME_CALCULATED = RESPONSE.body?.data?.income;

        if (!INCOME_CALCULATED) {
          this.snackbarService.openSnackBar(
            '❌ Ocurrió un error al calcular ingreso.',
            '',
          );
          this.isLoading = false;
          return;
        }

        INCOME_CALCULATED.anulacion = true;

        await this.postMovement(INCOME_CALCULATED);
      } catch (error) {
        this.snackbarService.openSnackBar(
          '❌ Ocurrió un error al calcular ingreso.',
          '',
        );
        this.isLoading = false;
      }
    } else {
      try {
        const URL_CALCULATE_EXPENSE: string = `expense/calculate/`;

        delete this.income.incomeDate;

        const RESPONSE = await this.movementsService.post(
          this.income,
          URL_CALCULATE_EXPENSE,
        );

        const EXPENSE_CALCULATED = RESPONSE.body?.data?.expense;

        if (!EXPENSE_CALCULATED) {
          this.snackbarService.openSnackBar(
            '❌ Ocurrió un error al calcular egreso.',
            '',
          );
          this.isLoading = false;
          return;
        }

        EXPENSE_CALCULATED.anulacion = true;

        await this.postMovement(EXPENSE_CALCULATED);
      } catch (error) {
        this.snackbarService.openSnackBar(
          '❌ Ocurrió un error al calcular egreso.',
          '',
        );
        this.isLoading = false;
      }
    }
  }

  async postMovement(income: Income): Promise<void> {
    if (!this.isExpense) {
      try {
        const URL_INCOME: string = `income/`;
        const RESPONSE = await this.movementsService.post(income, URL_INCOME);

        if (!RESPONSE?.body?.success) {
          this.snackbarService.openSnackBar(
            `❌ Error: ${
              RESPONSE.body?.responseMsg ?? 'No se pudo realizar el ingreso'
            }`,
            '',
          );
          return;
        }

        this.snackbarService.openSnackBar(
          '✅ Se realizó el ingreso correctamente',
          '',
        );
        this.submitForm();
      } catch (error) {
        this.snackbarService.openSnackBar(
          '❌ Ocurrió un error al realizar el ingreso.',
          '',
        );
      } finally {
        this.isLoading = false;
      }
    } else {
      try {
        const URL_EXPENSE: string = `expense/`;
        const RESPONSE = await this.movementsService.post(income, URL_EXPENSE);

        if (!RESPONSE?.body?.success) {
          this.snackbarService.openSnackBar(
            `❌ Error: ${
              RESPONSE.body?.responseMsg ?? 'No se pudo realizar el egreso'
            }`,
            '',
          );
          return;
        }

        this.snackbarService.openSnackBar(
          '✅ Se realizó el egreso correctamente',
          '',
        );
        this.submitForm();
      } catch (error) {
        this.snackbarService.openSnackBar(
          '❌ Ocurrió un error al realizar el egreso.',
          '',
        );
      } finally {
        this.isLoading = false;
      }
    }
  }

  submitForm() {
    this.formSubmit.emit(true);
  }

  // -----------------------------------
  // ---- INTERACCIÓN DEL USUARIO ------
  // -----------------------------------

  hasFormErrors(): { hasErrors: boolean; firstInputErrorID: string } {
    //Si alguna de las dos comisiones es > 0 permitimos que se envíe el form

    //Recorrer todos los campos buscando errores
    const INVALID_FIELDS: string[] = [];
    Object.keys(this.movementForm.controls).forEach((field) => {
      const CONTROL = this.movementForm.get(field);
      if (CONTROL && CONTROL.invalid) {
        INVALID_FIELDS.push(field);
      }
    });
    return {
      hasErrors: INVALID_FIELDS.length > 0,
      firstInputErrorID: INVALID_FIELDS[0],
    };
  }

  scrollToElementById(id: string) {
    const ELEMENT = document.getElementById(id);
    if (ELEMENT) {
      ELEMENT.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  setDataInForm(input: string, value: string | number | boolean): void {
    this.movementForm.get(input)?.setValue(value);
  }

  hasErrorInput(itemForm: string): boolean | undefined {
    return (
      this.movementForm.get(itemForm)?.touched &&
      this.movementForm.get(itemForm)?.invalid
    );
  }

  //Marcar input como tocado
  inputTouched(itemForm: string): void {
    this.movementForm.get(itemForm)?.markAsTouched();
  }

  //Marcar un campo como requerido
  markRequired(itemForm: string): void {
    this.movementForm.get(itemForm)?.setValidators([Validators.required]);
    this.movementForm.get(itemForm)?.updateValueAndValidity();
  }

  //Quitar validacion de requerido de un campo
  removeRequired(itemForm: string) {
    this.movementForm.get(itemForm)?.clearValidators();
    this.movementForm.get(itemForm)?.updateValueAndValidity();
  }

  showError(msg: string): void {
    this.snackbarService.openSnackBar('⛔ ' + msg, '');
  }

  cleanInput(number: InputTypes): number {
    if (number == null) return 0;
    let input = number.toString().trim();

    const LAST_DOT = input.lastIndexOf('.');
    const LAST_COMMA = input.lastIndexOf(',');

    let lastSeparator = Math.max(LAST_DOT, LAST_COMMA);
    if (lastSeparator !== -1) {
      const DECIMAL_PART = input.slice(lastSeparator + 1);
      const IS_LIKELY_DECIMAL =
        DECIMAL_PART.length > 0 && DECIMAL_PART.length <= 2;

      if (IS_LIKELY_DECIMAL) {
        const CLEAN =
          input.slice(0, lastSeparator).replace(/[.,]/g, '') +
          ',' +
          DECIMAL_PART;
        input = CLEAN;
      } else {
        input = input.replace(/[.,]/g, '');
      }
    }

    const DATA = input.replaceAll('.', '').replaceAll(',', '.') ?? '0';
    return Number(DATA);
  }

  isValidDecimalNumber(e: KeyboardEvent): void {
    const INPUT_ELEMENT = e.target as HTMLInputElement;
    const VALUE = INPUT_ELEMENT.value;

    // Handle replacing "." with ","
    if (e.key === '.' && !VALUE.includes(',')) {
      e.preventDefault();

      // Insert "," at the cursor position
      const SELECTION_START = INPUT_ELEMENT.selectionStart ?? 0;
      const SELECTION_END = INPUT_ELEMENT.selectionEnd ?? 0;
      const NEW_VALUE =
        VALUE.slice(0, SELECTION_START) + ',' + VALUE.slice(SELECTION_END);

      INPUT_ELEMENT.value = NEW_VALUE;

      // Position the cursor after the ","
      INPUT_ELEMENT.setSelectionRange(SELECTION_START + 1, SELECTION_START + 1);

      // Trigger the input event to register the change
      const EVENT = new Event('input', { bubbles: true });
      INPUT_ELEMENT.dispatchEvent(EVENT);

      return;
    }

    // Handle copy/paste/cut and allowed shortcuts
    const IS_CTRL_OR_CMD_SHORTCUT =
      (e.ctrlKey || e.metaKey) &&
      ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase());

    if (IS_CTRL_OR_CMD_SHORTCUT) {
      return; // Allow copy/paste (Ctrl+C, Ctrl+V, etc.)
    }

    // Handle other allowed keys like Backspace, Tab, Arrow keys, Delete
    const IS_ALLOWED_KEY =
      ['Tab', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(
        e.key,
      ) ||
      (e.key === ',' && !VALUE.includes(',')) ||
      (!isNaN(Number(e.key)) && e.key !== ' '); // Allow numbers and prevent space

    if (IS_ALLOWED_KEY) {
      return; // Allow these keys
    }

    // Prevent invalid input
    e.preventDefault();

    // Check for decimal places
    const [, DECIMAL_PART] = VALUE.split(',');

    // Prevent more than two decimal places
    if (DECIMAL_PART && DECIMAL_PART.length >= 2) {
      e.preventDefault();
    }
  }

  get voucherTypeScId(): number {
    return this.NODE === this.NODE_PY ? 3 : 4;
  }

  get hideTaxFieldsForSc(): boolean {
    return this.isExpense && this.income?.voucherType === this.voucherTypeScId;
  }
}
